// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useMemo, useState } from 'react';
import { ICONS } from '@Constants/iconsApp';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import { ObjectTypeV1 as ObjectType, UserOutput, AdminPermissionsEnum } from 'thehive-sdk';
import useConfigList from '@Hooks/useConfigList';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import client from '@Client';
import RouteBuilder from '@Services/RouteBuilder';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { computeEventList as computeUserEventList } from '@Organisms/User/eventList';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { Avatar, Space } from 'antd';
import THTag from '@Atoms/THTag/THTag';
import { HTMLColors } from '@Constants/colors';
import { useHistory } from 'react-router';
import { FormatMultipleDate } from '@Molecules/Format';
import { GlobalUserDetails } from '@Organisms/User/GlobalUserDetails/GlobalUserDetails';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import ActionList from '@Molecules/ActionList/ActionList';
import { SelectedInfo } from '@Atoms/SelectedInfo/SelectedInfo';
import { StatusContext } from '@Context/StatusProvider';
import { RouteType } from '@Types';
import { Link } from 'react-router-dom';
import { useDrawer } from '@Hooks/useDrawer';
import { GlobalUserDrawer } from '@Organisms/User/GlobalUserDrawer/GlobalUserDrawer';
import { GlobalUserCreate } from '@Organisms/User/GlobaUserCreate/GlobalUserCreate';
import { ExportButton } from '@Organisms/Export/ExportDrawer/ExportButton';
import UserMFAButton from '@Atoms/Buttons/UserMFAButton/UserMFAButton';

import './GlobalUsersList.scss';

const GlobalUsersList = (): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();

    const { quotas } = useContext(StatusContext);

    const { visible, setVisible, data, setData } = useDrawer<UserOutput>({});
    const { visible: visibleCreate, setVisible: setVisibleCreate } = useDrawer<UserOutput>({});

    const config = useConfigList<UserOutput>({
        selector: {
            head: [{ name: 'listUser' }],
            name: 'get-every-users',
            extraData: ['organisations', 'lockout'],
            sort: ['+locked', '+login'],
        },
        controller: client.UserController,
        storageId: StorageFilterEnum.GLOBAL_USERS,
        objectType: ObjectType.USER,
    });

    const {
        actionListRenderer,
        columnsRenderer,
        countRefetch,
        dataSource = [],
        filterManager,
        listRefetch,
        paginationManager,
        rowSelectionCustomRenderer,
        stateSelectedKeys: { selectedKeys },
        status = 'loading',
        storagePageManager,
        storageViewManager,
        table,
        queryParams,
    } = config;

    const eventList = useMemo(
        () =>
            computeUserEventList({
                controller: client.UserController,
                locale: i,
                onSuccess: (count, message) => {
                    listRefetch();
                    if (count) {
                        countRefetch();
                    }
                    if (message) {
                        openMessage('success', message);
                    }
                },
                onError: handleNotificationError,
            }),
        [i, listRefetch, countRefetch],
    );

    useDrawer.watch(dataSource, () => {
        if (data) {
            setData(dataSource.find(({ _id }) => _id === data._id));
        }
    });

    const [exportDrawerVisible, setExportDrawerVisible] = useState(false);

    const actionList = actionListRenderer((selectedItems, onSuccess) => {
        const userIdList = selectedItems.map(({ _id }) => _id);
        const isMultiple = userIdList.length > 1 ? 'multiple' : 'single';

        return {
            lock: {
                query: (): Promise<void> =>
                    client.UserController.multipleEdit({ locked: true }, userIdList)
                        .then(() => {
                            openMessage('success', i('notification.user.edit'));
                            onSuccess();
                        })
                        .catch(handleNotificationError),
                message: i(`user.actions.lock.${isMultiple}`),
                label: i('user.actions.lock.label'),
                icon: <ICONS.LockIcon />,
                hidden: selectedItems.some(({ locked }) => locked),
                confirm: true,
            },
            unlock: {
                query: (): Promise<void> =>
                    client.UserController.multipleEdit({ locked: false }, userIdList)
                        .then(() => {
                            openMessage('success', i('notification.user.edit'));
                            onSuccess();
                        })
                        .catch(handleNotificationError),
                message: i(`user.actions.unlock.${isMultiple}`),
                label: i('user.actions.unlock.label'),
                icon: <ICONS.UnlockIcon />,
                hidden: selectedItems.some(({ locked }) => !locked),
            },
        };
    });

    const columns = columnsRenderer([
        {
            className: 'global-users-list__locked',
            render: (data, { bool: filterBool }) => {
                const isBlocked = data.extraData?.lockout?.lockedUntil;

                return (
                    <THTag
                        full
                        value={i(`user.drawer.status.${data.locked ? 'locked' : 'active'}`)}
                        color={data.locked ? HTMLColors.RED : isBlocked ? HTMLColors.AMBER : HTMLColors.GREEN}
                        onClick={() => filterBool('locked', data.locked + '')}
                    />
                );
            },
        },
        {
            title: [
                { label: i('common.name'), sortField: 'name' },
                { label: i('common.login'), sortField: 'login' },
            ],
            className: 'global-users-list__details-column column_preview',
            render: (data) => (
                <GlobalUserDetails
                    data={data}
                    onPreviewClick={() => {
                        setVisible(true);
                        setData(data);
                    }}
                />
            ),
        },
        {
            title: i('common.organisations'),
            className: 'global-users-list__organisations',
            render: ({ extraData }) => (
                <Avatar.Group maxCount={3} size="large">
                    {extraData?.organisations
                        ?.sort(({ organisation: orgA }, { organisation: orgB }) => orgA.localeCompare(orgB))
                        .map(({ organisation, avatar }) => (
                            <UserAvatar
                                key={organisation}
                                name={organisation}
                                avatar={avatar}
                                showTooltip
                                size="large"
                                onClick={() =>
                                    history.push(RouteBuilder.compute('administration.users', { orgId: organisation }))
                                }
                            />
                        ))}
                </Avatar.Group>
            ),
        },
        {
            title: [{ label: i('user.list.table.columns.mfa.label'), sortField: 'hasMFA' }],
            className: 'global-users-list__mfa',
            render: (data, { bool: filterBool }) => {
                const { hasMFA } = data;

                return (
                    <UserMFAButton
                        hasMFA={hasMFA}
                        onClick={() => filterBool('hasMFA', hasMFA + '')}
                        tooltip={i(`user.list.table.columns.mfa.${hasMFA ? 'enabled' : 'not-activated'}`)}
                    />
                );
            },
        },
        {
            title: [{ label: i('common.created-by'), sortField: '_createdBy' }],
            className: 'global-users-list__created-by',
            render: ({ _createdBy }) => <AssigneeInfo assignee={_createdBy} />,
        },
        {
            title: [
                { label: i('common.dates') },
                { label: 'C.', sortField: '_createdAt' },
                { label: 'U.', sortField: '_updatedAt' },
            ],
            className: 'global-users-list__created-at',
            render: ({ _createdAt, _updatedAt = 0 }, { date: filterDate }) => (
                <FormatMultipleDate
                    dates={[
                        {
                            label: 'C.',
                            value: _createdAt,
                            onClick: (): void => filterDate(_createdAt, '_createdAt'),
                        },
                        {
                            label: 'U.',
                            value: _updatedAt,
                            onClick: (): void => filterDate(_updatedAt, '_updatedAt'),
                        },
                    ]}
                />
            ),
        },
    ]);

    const rowSelection = rowSelectionCustomRenderer({
        label: i('common.selected'),
        render: ({ locked }, checkbox): ReactElement => (
            <Space direction="vertical" size={1}>
                {checkbox}
                {locked && <ICONS.LockFilled className="user-list-checkbox-cell__lock" />}
            </Space>
        ),
    });

    const { data: describe } = useListAllFields();

    return (
        <>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        <AddButton
                            label={i('user.list.header.add-user')}
                            disabled={!quotas.users?.normal.valid && !quotas.users?.service.valid}
                            onClick={() => setVisibleCreate(true)}
                        />

                        {storagePageManager && storageViewManager ? (
                            <ManageViewList
                                pageManager={storagePageManager}
                                viewManager={storageViewManager}
                                beforeCreate={(content: any): any => {
                                    filterManager.applyFilters();
                                    return { ...content, filters: filterManager.tempFilters };
                                }}
                            />
                        ) : null}

                        <ExportButton
                            onClick={() => {
                                setExportDrawerVisible(true);
                            }}
                            objectType="User"
                            selectedItems={dataSource.reduce((acc: string[], data) => {
                                if (selectedKeys.includes(data['_id'])) {
                                    acc.push(data['_id']);
                                }
                                return acc;
                            }, [])}
                            queryParams={queryParams}
                            visible={exportDrawerVisible}
                            afterVisible={setExportDrawerVisible}
                            listNumber={table.count}
                        />

                        <SelectedInfo label={i('common.selected')} count={selectedKeys.length} />

                        {
                            <ActionList
                                actions={actionList(
                                    dataSource.filter((data: { _id: string }) => selectedKeys.includes(data['_id'])),
                                    (count) => {
                                        listRefetch?.();
                                        table?.setSelectedKeys?.([]);
                                        if (count) {
                                            countRefetch?.();
                                        }
                                    },
                                )}
                                scope="list"
                                hidden={!selectedKeys?.length}
                            />
                        }
                    </THList.Slot>
                    <THList.Slot name="NAVIGATION">
                        <ManageSwitch
                            label={i('common.filters')}
                            value={filterManager.editFilters}
                            onChange={(editFilters): void => {
                                filterManager.setEditFilters(editFilters);
                            }}
                            icon={<ICONS.FilterOutlined />}
                        />
                    </THList.Slot>
                    <THList.Slot name="CONTENT">
                        {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                            <THFilter
                                filterConfig={filterManager}
                                describeField={describe?.[ObjectType.USER]}
                                fieldsToExclude={['profile']}
                            >
                                <Footer filterConfig={filterManager} />
                            </THFilter>
                        )}
                    </THList.Slot>
                </THList.Header>

                <THList.Content
                    rowSelection={rowSelection}
                    dataSource={dataSource}
                    columns={columns}
                    rowKey="_id"
                    emptyPlaceholder={
                        <DisplayEmptyData spin={status === 'loading'} label={i('user.none')}>
                            <THButton type="link">{i('user.list.header.add-user')}</THButton>
                        </DisplayEmptyData>
                    }
                />

                <THList.Footer>
                    <THList.Slot name="NAVIGATION">
                        <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                        <PaginationSize paginationConfig={paginationManager} />
                    </THList.Slot>
                </THList.Footer>
            </THList>

            {data && visible && (
                <GlobalUserDrawer visible={visible} onVisibleChange={setVisible} data={data} eventList={eventList} />
            )}
            {visibleCreate && (
                <GlobalUserCreate
                    visible={visibleCreate}
                    onVisibleChange={setVisibleCreate}
                    onFinish={(user, organisations) => {
                        eventList.onCreate(user).then((user) => {
                            user && eventList.onSetOrganisations(organisations, user._id).then(() => listRefetch());
                        });
                    }}
                />
            )}
        </>
    );
};
const path = RouteBuilder.compute('administration.global-users');

const MenuLink = (): ReactElement => {
    const local = useLocalMessage();
    return <Link to={path}>{local('mainLayout.adminMenu.global-users')}</Link>;
};

export const Route: RouteType = {
    path,
    menu: {
        key: 'users',
        icon: <ICONS.UsersIcon />,
        render: <MenuLink />,
        selectTab: path,
        space: ['admin'],
        group: 'config',
    },
    permissionNeeded: [AdminPermissionsEnum.MANAGE_USER],
    breadcrumb: { label: 'breadcrumb.global.global-users' },
    content: <GlobalUsersList />,
    space: ['admin'],
    exact: true,
};
